import './Skeleton.css'

export function Skeleton({ width = '100%', height = '20px', borderRadius = '8px', style = {}, className = '' }) {
  return (
    <div 
      className={`skeleton-pulse ${className}`}
      style={{ width, height, borderRadius, ...style }} 
    />
  )
}

export function SkeletonCard({ height = '120px' }) {
  return (
    <div className="skeleton-card">
      <Skeleton height={height} borderRadius="var(--border-radius-lg)" />
    </div>
  )
}

export function SkeletonRow({ count = 5 }) {
  return (
    <div className="skeleton-rows">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-row">
          <Skeleton width="40px" height="40px" borderRadius="50%" />
          <div className="skeleton-row-text">
            <Skeleton width={`${60 + Math.random() * 30}%`} height="14px" />
            <Skeleton width={`${40 + Math.random() * 20}%`} height="12px" style={{ opacity: 0.6 }} />
          </div>
          <Skeleton width="80px" height="32px" borderRadius="6px" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonChart({ height = '240px' }) {
  return (
    <div className="skeleton-chart-container">
      <Skeleton width="40%" height="18px" style={{ marginBottom: '16px' }} />
      <Skeleton height={height} borderRadius="var(--border-radius-md)" />
    </div>
  )
}

export function SkeletonGrid({ cols = 3, rows = 1, height = '100px' }) {
  return (
    <div className="skeleton-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {Array.from({ length: cols * rows }).map((_, i) => (
        <Skeleton key={i} height={height} borderRadius="var(--border-radius-lg)" />
      ))}
    </div>
  )
}

/* ============================================
   Page-Specific Skeleton Layouts
   ============================================ */

export function DashboardSkeleton() {
  return (
    <div className="skeleton-dashboard">
      {/* Top banner */}
      <div className="skeleton-pulse skeleton-banner" />
      
      {/* 4 stat cards */}
      <div className="skeleton-stats">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-pulse skeleton-stat-card" />
        ))}
      </div>

      {/* Two panels */}
      <div className="skeleton-panels">
        <div className="skeleton-pulse skeleton-panel-wide" />
        <div className="skeleton-pulse skeleton-panel-narrow" />
      </div>

      {/* Finance title + 4 cards */}
      <div className="skeleton-pulse skeleton-finance-title" />
      <div className="skeleton-stats">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-pulse skeleton-stat-card" />
        ))}
      </div>
    </div>
  )
}

export function OperationsSkeleton() {
  return (
    <div className="skeleton-operations">
      {/* Tabs */}
      <div className="skeleton-op-header">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-pulse skeleton-op-tab" />
        ))}
      </div>
      {/* Operation cards */}
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="skeleton-pulse skeleton-op-card" />
      ))}
    </div>
  )
}

export function PaymentsSkeleton() {
  return (
    <div className="skeleton-payments">
      {/* Summary card */}
      <div className="skeleton-pulse skeleton-pay-summary" />
      
      {/* Employee salary rows */}
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="skeleton-pay-row">
          <div className="skeleton-pulse skeleton-pay-avatar" />
          <div className="skeleton-pay-info">
            <Skeleton width={`${55 + i * 8}%`} height="16px" />
            <Skeleton width={`${35 + i * 5}%`} height="12px" style={{ opacity: 0.5 }} />
          </div>
          <div className="skeleton-pulse skeleton-pay-amount" />
        </div>
      ))}
    </div>
  )
}

export function ScheduleSkeleton() {
  return (
    <div className="skeleton-schedule">
      {/* Day headers */}
      <div className="skeleton-sched-header">
        {[1, 2, 3, 4, 5, 6, 7].map(i => (
          <div key={i} className="skeleton-pulse skeleton-sched-day" />
        ))}
      </div>

      {/* Employee rows */}
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="skeleton-sched-row">
          <div className="skeleton-pulse skeleton-sched-name" />
          <div className="skeleton-sched-cells">
            {[1, 2, 3, 4, 5, 6, 7].map(j => (
              <div key={j} className="skeleton-pulse skeleton-sched-cell" />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function SettingsSkeleton() {
  return (
    <div className="skeleton-settings">
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton-set-section">
          <div className="skeleton-pulse skeleton-set-title" />
          <div className="skeleton-pulse skeleton-set-field" />
          <div className="skeleton-pulse skeleton-set-field" />
          {i === 1 && <div className="skeleton-pulse skeleton-set-field" />}
        </div>
      ))}
    </div>
  )
}

export function ChatSkeleton() {
  return (
    <div className="skeleton-chat">
      {/* Chat bubbles alternating left/right */}
      <div className="skeleton-chat-bubble">
        <div className="skeleton-pulse skeleton-chat-avatar" />
        <div className="skeleton-pulse skeleton-chat-msg" style={{ width: '55%', height: '48px' }} />
      </div>
      <div className="skeleton-chat-bubble right">
        <div className="skeleton-pulse skeleton-chat-avatar" />
        <div className="skeleton-pulse skeleton-chat-msg" style={{ width: '40%', height: '36px' }} />
      </div>
      <div className="skeleton-chat-bubble">
        <div className="skeleton-pulse skeleton-chat-avatar" />
        <div className="skeleton-pulse skeleton-chat-msg" style={{ width: '65%', height: '60px' }} />
      </div>
      <div className="skeleton-chat-bubble right">
        <div className="skeleton-pulse skeleton-chat-avatar" />
        <div className="skeleton-pulse skeleton-chat-msg" style={{ width: '45%', height: '44px' }} />
      </div>
      <div className="skeleton-chat-bubble">
        <div className="skeleton-pulse skeleton-chat-avatar" />
        <div className="skeleton-pulse skeleton-chat-msg" style={{ width: '50%', height: '36px' }} />
      </div>
    </div>
  )
}

export function ViolationsSkeleton() {
  return (
    <div className="skeleton-violations">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="skeleton-pulse skeleton-viol-card" />
      ))}
    </div>
  )
}

export function AnalyticsSkeleton() {
  return (
    <div className="skeleton-analytics">
      <div className="skeleton-an-kpis">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-pulse skeleton-an-kpi" />
        ))}
      </div>
      <div className="skeleton-pulse skeleton-an-chart" />
      <div className="skeleton-pulse skeleton-an-table" />
    </div>
  )
}

export function DeductionsSkeleton() {
  return (
    <div className="skeleton-violations">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="skeleton-pulse skeleton-viol-card" />
      ))}
    </div>
  )
}

export default Skeleton
